import { cloneElement, useId } from "react";
import { twMerge } from "tailwind-merge";

interface TooltipProps {
  label: string;
  children: React.ReactElement;
  side?: "top" | "bottom";
  className?: string;
}

// Lightweight, dependency-free tooltip: shows on hover and on keyboard focus
// (not hover-only, so it's usable without a mouse), wired via aria-describedby
// so screen readers announce it too.
const Tooltip = ({ label, children, side = "top", className }: TooltipProps) => {
  const id = useId();

  return (
    <span className="relative inline-flex group/tooltip">
      {cloneElement(children, {
        "aria-describedby": children.props["aria-describedby"]
          ? `${children.props["aria-describedby"]} ${id}`
          : id,
      })}
      <span
        role="tooltip"
        id={id}
        className={twMerge([
          "pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-control bg-primary px-2.5 py-1.5 text-white shadow-raised",
          "opacity-0 scale-95 transition-[opacity,transform] duration-150 ease-out",
          "group-hover/tooltip:opacity-100 group-hover/tooltip:scale-100",
          "group-focus-within/tooltip:opacity-100 group-focus-within/tooltip:scale-100",
          side === "top" && "bottom-full mb-2",
          side === "bottom" && "top-full mt-2",
          className,
        ])}
      >
        <span className="text-xs font-medium">{label}</span>
      </span>
    </span>
  );
};

export default Tooltip;
